import { c as _c } from "react/compiler-runtime";
import { BadgeList } from '@/components/Atoms/BadgeList/BadgeList';
import { CardLinks } from '@/components/Atoms/CardLinks/CardLinks';
import { SkeletonPulse } from '@/components/Atoms/SkeletonPulse/SkeletonPulse';
import { Card, CardBody } from '@/components/Molecules/Card/Card';
import { ContentPin } from '@/components/Molecules/ContentPin/ContentPin';
import { ContentTypeHeader } from '@/components/Molecules/ContentTypeHeader/ContentTypeHeader';
import type { IContentGroupsProps } from '@/components/Organisms/ContentGroups/ContentGroups';
import { useFetchResponseHandler } from '@/hooks/useFetchResponseHandler';
import useTranslation from '@/hooks/useTranslation';
import type { IPinableItem, ISectorPage } from '@/interfaces';
import { ContentType } from '@/interfaces';
import { BrowserHttpClient } from '@/services/Http/BrowserHttpClient';
import { adjustImageAsset } from '@/utils/imageUtils';
import type { ComponentProps, FC } from 'react';
interface ISectorCardProps extends Omit<ComponentProps<typeof Card>, 'title'> {
  sector: ISectorPage & IPinableItem;
  pinned?: boolean;
}

/**
 * A responsive card for a sector item with an image and title.
 */
export const SectorCard: FC<ISectorCardProps> = t0 => {
  const $ = _c(37);
  let pinned;
  let rest;
  let sector;
  if ($[0] !== t0) {
    ({
      sector,
      pinned,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = pinned;
    $[2] = rest;
    $[3] = sector;
  } else {
    pinned = $[1];
    rest = $[2];
    sector = $[3];
  }
  const {
    t
  } = useTranslation();
  let t1;
  if ($[4] !== sector.id) {
    t1 = () => new BrowserHttpClient().get(`/sector_pages/${sector.id}`);
    $[4] = sector.id;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    data,
    error
  } = useFetchResponseHandler(t1, t2);
  let links;
  if (data === undefined && !error) {
    let t3;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <SkeletonPulse lines={3} />;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    links = t3;
  } else {
    if (data?.researchGroups?.length) {
      let t3;
      if ($[8] !== data.researchGroups || $[9] !== sector.url) {
        let t4;
        if ($[11] !== sector.url) {
          t4 = group => ({
            children: group.title,
            href: `${sector.url}?research=${group.id}#research`
          });
          $[11] = sector.url;
          $[12] = t4;
        } else {
          t4 = $[12];
        }
        t3 = data.researchGroups.map(t4);
        $[8] = data.researchGroups;
        $[9] = sector.url;
        $[10] = t3;
      } else {
        t3 = $[10];
      }
      let t4;
      if ($[13] !== t3) {
        t4 = <CardLinks links={t3} />;
        $[13] = t3;
        $[14] = t4;
      } else {
        t4 = $[14];
      }
      links = t4;
    }
  }
  let t3;
  if ($[15] !== sector.leadImage) {
    t3 = sector.leadImage ? [adjustImageAsset(sector.leadImage.baseUrl, 600), adjustImageAsset(sector.leadImage.baseUrl, 1200)] : undefined;
    $[15] = sector.leadImage;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const t4 = sector.url;
  const t5 = sector.title;
  let t6;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ContentTypeHeader type={ContentType.sector} />;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== pinned || $[19] !== sector.pinned) {
    t7 = (pinned || sector.pinned) && <ContentPin />;
    $[18] = pinned;
    $[19] = sector.pinned;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t7) {
    t8 = <BadgeList>{t6}{t7}</BadgeList>;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t) {
    t9 = t("common:Sections.research");
    $[23] = t;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t9) {
    t10 = <h4 className="cds-t-label">{t9}</h4>;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== links || $[28] !== t10) {
    t11 = <CardBody>{t10}{links}</CardBody>;
    $[27] = links;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== rest || $[31] !== sector.title || $[32] !== sector.url || $[33] !== t11 || $[34] !== t3 || $[35] !== t8) {
    t12 = <Card headerImage={t3} boxed={true} withImage={false} href={t4} title={t5} prefix={t8} data-testid="sector-card" {...rest}>{t11}</Card>;
    $[30] = rest;
    $[31] = sector.title;
    $[32] = sector.url;
    $[33] = t11;
    $[34] = t3;
    $[35] = t8;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  return t12;
};